import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarDays, FileText, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";
import { API_BASE } from "@/lib/track";
import { LogoMark } from "@/components/Logo";

const prettyTime = (t) => {
  const [h, m] = t.split(":").map(Number);
  const am = h < 12;
  return `${(h + 11) % 12 + 1}:${String(m).padStart(2, "0")} ${am ? "AM" : "PM"}`;
};

const prettyDate = (d) =>
  new Date(`${d}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

export default function PortalPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) {
      setState("error");
      return;
    }
    axios
      .get(`${API_BASE}/portal/${encodeURIComponent(token)}`)
      .then(({ data: d }) => {
        setData(d);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [token]);

  const status = data?.status || "";
  const steps = [
    { label: "Intake received", done: true },
    { label: "Analysis underway", done: ["analysis_underway", "draft_ready", "report_ready"].includes(status) },
    { label: "Report ready", done: status === "report_ready" },
  ];

  return (
    <div className="min-h-screen bg-obsidian px-5 py-14 sm:py-20 grain">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="portal-back-link"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold transition-colors"
        >
          All In Hand Marketing
        </Link>

        {state === "loading" && (
          <div className="mt-24 text-center text-slate-400 flex items-center justify-center gap-3" data-testid="portal-loading">
            <Loader2 className="w-5 h-5 animate-spin text-gold" /> Loading your portal…
          </div>
        )}

        {state === "error" && (
          <div className="mt-16 rounded-3xl border border-slate-700/60 bg-obsidian-800/60 p-10 text-center" data-testid="portal-error">
            <h1 className="font-display text-2xl font-bold text-white">Portal not found</h1>
            <p className="text-slate-400 text-sm mt-3">
              This portal link is invalid or has expired. Use the link from your email, or contact us
              and we'll resend it.
            </p>
            <Link
              data-testid="portal-error-home"
              to="/"
              className="inline-block mt-8 rounded-full border border-gold/60 text-gold font-display font-bold px-7 py-3.5 hover:bg-gold hover:text-obsidian transition-colors"
            >
              Back to Home
            </Link>
          </div>
        )}

        {state === "ok" && data && (
          <div className="mt-8" data-testid="portal-dashboard">
            <div className="flex items-center gap-4">
              <LogoMark className="w-12 h-12" />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">My All In Hand</p>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">
                  Welcome{data.first_name ? `, ${data.first_name}` : ""}
                </h1>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-8" data-testid="portal-status-steps">
              {steps.map((s, i) => (
                <div
                  key={s.label}
                  className={`rounded-2xl border p-5 ${
                    s.done ? "border-emerald-500/40 bg-emerald-500/5" : "border-slate-700/60 bg-obsidian-800/50"
                  }`}
                >
                  <p className={`font-mono text-[10px] tracking-[0.25em] uppercase ${s.done ? "text-emerald-400" : "text-slate-500"}`}>
                    Step {i + 1} {s.done && "✓"}
                  </p>
                  <p className={`font-display font-semibold mt-2 text-sm ${s.done ? "text-white" : "text-slate-400"}`}>
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            {data.report ? (
              <div className="mt-8 rounded-3xl border border-gold/25 bg-obsidian-800/60 p-7 sm:p-10" data-testid="portal-report">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gold" />
                  <h2 className="font-display text-xl font-bold text-white">
                    {data.offer_type === "express" ? "Your Business Snapshot" : "Your Growth Assessment Report"}
                  </h2>
                </div>
                <div className="mt-6 space-y-6">
                  {data.report.map((s, i) => (
                    <div key={i}>
                      <h3 className="font-display font-semibold text-gold text-sm">{s.title}</h3>
                      <p className="text-slate-300 text-sm leading-relaxed mt-2">{s.body}</p>
                    </div>
                  ))}
                </div>
                <p className="text-slate-600 text-xs mt-6 leading-relaxed">
                  Findings are informational and prepared with AI assistance under human review. They
                  do not guarantee rankings, leads, revenue, or sales.
                </p>
              </div>
            ) : (
              <div className="mt-8 rounded-3xl border border-slate-700/60 bg-obsidian-800/60 p-7 sm:p-10 text-center" data-testid="portal-analysis">
                <Loader2 className="w-6 h-6 text-gold animate-spin mx-auto" />
                <p className="text-slate-300 text-sm mt-4">
                  Your analysis is underway. Your reviewed report will appear here — we'll email you
                  the moment it's ready.
                </p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="rounded-2xl border border-slate-700/60 bg-obsidian-800/50 p-6" data-testid="portal-meeting">
                <div className="flex items-center gap-2.5">
                  <CalendarDays className="w-4 h-4 text-emerald-400" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Strategy session</p>
                </div>
                {data.appointment ? (
                  <p className="font-display font-bold text-white mt-3 text-sm">
                    {prettyDate(data.appointment.date)} · {prettyTime(data.appointment.time)}
                  </p>
                ) : (
                  <p className="text-slate-400 text-xs mt-3">
                    Not booked yet — check your email link or contact us to schedule.
                  </p>
                )}
              </div>

              <div className="rounded-2xl border border-slate-700/60 bg-obsidian-800/50 p-6" data-testid="portal-purchase">
                <div className="flex items-center gap-2.5">
                  <ShieldCheck className="w-4 h-4 text-gold" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Purchase</p>
                </div>
                {data.payment ? (
                  <p className="font-display font-bold text-white mt-3 text-sm">
                    ${(data.payment.amount / 100).toFixed(2)} · {data.payment.status}
                  </p>
                ) : (
                  <p className="text-slate-400 text-xs mt-3">No purchase attached to this portal.</p>
                )}
                <p className="text-slate-600 text-[10px] mt-1">7-day money-back guarantee on eligible purchases</p>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-700/60 bg-obsidian-900/60 p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="portal-support">
              <p className="text-slate-400 text-sm">Questions, or ready for your next step?</p>
              <Link
                to="/intake?type=consultation"
                data-testid="portal-support-cta"
                className="text-center shrink-0 rounded-full border border-gold/60 text-gold font-display font-bold px-6 py-3 text-sm hover:bg-gold hover:text-obsidian transition-colors"
              >
                Book a Consultation
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
